import { create, StateCreator } from './store';

type WithDirectStateAccess<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

export function createEnhancedStore<T>(
  createState: StateCreator<T>
) {
  const store = create(createState);
  const state = store.getState() as object;

  const useModel = Object.keys(state).reduce((total, key) => {
    Object.defineProperty(total, key, {
      get() {
        return store((s) => s[key as keyof T]);
      },
      enumerable: true,
    });
    return total;
  }, {} as { [K in keyof T]: () => T[K] });

  (store as any).use = useModel;

  return store as WithDirectStateAccess<typeof store>;
}

export type StoreType<T> = ReturnType<typeof createEnhancedStore<T>>;
export type StoreState<T> = ReturnType<StoreType<T>['getState']>;

export function createStore<T extends object>(
  createState: StateCreator<T>
) {
  const store = createEnhancedStore(createState);

  function useAppStore(): T {
    return store.use as T;
  }

  return {
    useAppStore,
    subscribe: store.subscribe,
    getState: store.getState,
    setState: store.setState,
  }
}